import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { UserPlus, Loader2, Copy, Shield, Wallet, Code, Palette, CheckCircle2 } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS_COLABORADOR = [
  { id: "admin_operacional", nome: "Admin Operacional", desc: "Gerencia pedidos, usuários, dia a dia", icon: Shield, cor: "bg-blue-600" },
  { id: "admin_financeiro", nome: "Admin Financeiro", desc: "Vê dinheiro, mas não autoriza repasse sozinho", icon: Wallet, cor: "bg-emerald-600" },
  { id: "admin_dev_backend", nome: "Dev Backend", desc: "Acesso técnico ao código e às APIs", icon: Code, cor: "bg-purple-600" },
  { id: "admin_dev_frontend", nome: "Dev Frontend / Design", desc: "Acesso às telas visuais do sistema", icon: Palette, cor: "bg-pink-600" },
];

/**
 * Tela exclusiva do dono (super_admin) para criar contas de
 * administrador/colaborador - regra de negócio confirmada: ninguém se
 * auto-cadastra como admin, só o dono cria e envia a senha manualmente.
 */
const CriarColaborador = () => {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [tipo, setTipo] = useState("admin_operacional");
  const [criando, setCriando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const criar = async () => {
    if (!nome || !email) {
      toast.error("Preencha nome e e-mail.");
      return;
    }
    setCriando(true);
    try {
      const resp = await authService.fetchAuth(`${BACKEND_URL}/api/admin/criar-colaborador`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome, email, tipo }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao criar colaborador");
      setResultado(data);
      toast.success("Colaborador criado! Copie a senha abaixo.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCriando(false);
    }
  };

  const copiar = (texto, label) => {
    navigator.clipboard.writeText(texto);
    toast.success(`${label} copiado!`);
  };

  const novoColaborador = () => {
    setResultado(null);
    setNome("");
    setEmail("");
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <UserPlus className="w-7 h-7 text-orange-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Criar Administrador</h1>
          <p className="text-sm text-gray-400">Só você pode criar essas contas - o colaborador nunca se cadastra sozinho</p>
        </div>
      </div>

      {resultado ? (
        <Card className="p-6 bg-gray-800/50 border-green-700">
          <div className="flex items-center gap-2 mb-4 text-green-400">
            <CheckCircle2 className="w-5 h-5" />
            <p className="font-medium">Conta criada com sucesso</p>
          </div>
          <p className="text-sm text-gray-400 mb-1">Envie estes dados ao colaborador por um canal seguro (WhatsApp, e-mail direto):</p>

          <div className="bg-gray-900 rounded-lg p-4 space-y-3 mt-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">E-mail</p>
                <p className="text-white font-mono">{resultado.email}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => copiar(resultado.email, "E-mail")}>
                <Copy className="w-3 h-3" />
              </Button>
            </div>
            <div className="flex items-center justify-between border-t border-gray-800 pt-3">
              <div>
                <p className="text-xs text-gray-500">Senha (gerada automaticamente)</p>
                <p className="text-white font-mono">{resultado.senha_gerada}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => copiar(resultado.senha_gerada, "Senha")}>
                <Copy className="w-3 h-3" />
              </Button>
            </div>
          </div>

          <p className="text-xs text-yellow-500 mt-3">⚠️ {resultado.aviso}</p>

          <Button onClick={novoColaborador} className="w-full mt-4 bg-orange-600 hover:bg-orange-500">
            Criar outro colaborador
          </Button>
        </Card>
      ) : (
        <Card className="p-6 bg-gray-800/50 border-gray-700">
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Nome do colaborador</label>
              <Input placeholder="Nome completo" value={nome} onChange={(e) => setNome(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">E-mail dele</label>
              <Input type="email" placeholder="colaborador@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Tipo de acesso</label>
              <div className="grid grid-cols-2 gap-2">
                {TIPOS_COLABORADOR.map((t) => {
                  const Icone = t.icon;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setTipo(t.id)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        tipo === t.id ? `${t.cor} border-transparent text-white` : "bg-gray-900 border-gray-700 text-gray-400 hover:border-gray-600"
                      }`}
                    >
                      <Icone className="w-4 h-4 mb-1" />
                      <p className="text-sm font-medium">{t.nome}</p>
                      <p className="text-[10px] opacity-80 mt-0.5">{t.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <Button onClick={criar} disabled={criando} className="w-full mt-6 bg-orange-600 hover:bg-orange-500">
            {criando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Criar Colaborador
          </Button>
        </Card>
      )}
    </div>
  );
};

export default CriarColaborador;
